// ─── Routes ───────────────────────────────────────────────────────────────────
export const ROUTE_PATHS = { HOME: '/' } as const;

// ─── Types ────────────────────────────────────────────────────────────────────
export interface SectorStat {
  value: string;
  label: string;
  note: string;
}

export interface ProcessStep {
  num: string;
  title: string;
  description: string;
}

export interface CaseStudy {
  id: string;
  title: string;
  location: string;
  sector: string;
  badge: 'EXITOSO' | 'MEJORAS' | 'ERRORES';
  metric: string;
  metricLabel: string;
  insight: string;
  description: string;
  image: string;
}

export interface GalleryImage {
  url: string;
  alt: string;
  span?: 'col' | 'row';
}

// ─── Macro Stats (Hero Row) ───────────────────────────────────────────────────
export const MACRO_STATS = [
  {
    sector: 'Restaurantes',
    headline: '+18%',
    sub: 'ticket promedio',
    note: 'sin cambiar precios, solo diseño',
  },
  {
    sector: 'Airbnb',
    headline: '$180',
    sub: 'USD/noche',
    note: 'vs $70 USD estándar en CDMX',
  },
  {
    sector: 'Hoteles',
    headline: '+41%',
    sub: 'tarifa boutique',
    note: 'vs hotel estándar misma categoría',
  },
];

// ─── Restaurantes Stats ───────────────────────────────────────────────────────
export const RESTAURANTE_STATS: SectorStat[] = [
  { value: '+18%', label: 'ticket promedio', note: 'sin cambiar el menú' },
  { value: '68.5%', label: 'eligen por ambiente', note: 'sobre conveniencia o precio' },
  { value: '12–24m', label: 'recuperas inversión', note: 'ROI 70%–100% garantizado' },
];

// ─── Airbnb Stats ─────────────────────────────────────────────────────────────
export const AIRBNB_STATS: SectorStat[] = [
  { value: '×2.5', label: 'tarifa por noche', note: '$180 vs $70 USD en CDMX' },
  { value: '+20%', label: 'ingresos anuales', note: 'con diseño estratégico' },
  { value: '<12m', label: 'recuperas tu inversión', note: '$5,000–$10,000 USD upgrade' },
];

// ─── Hotel Stats ──────────────────────────────────────────────────────────────
export const HOTEL_STATS: SectorStat[] = [
  { value: '+41%', label: 'tarifa premium', note: 'boutique vs hotel estándar' },
  { value: '-30%', label: 'costo de marketing', note: 'efecto "Instagrammable"' },
  { value: '+15%', label: 'plusvalía del inmueble', note: 'sobre propiedades sin diseño' },
];

// ─── Process Steps ────────────────────────────────────────────────────────────
export const PROCESS_STEPS: ProcessStep[] = [
  {
    num: '01',
    title: 'Diagnóstico de tu espacio',
    description:
      'Analizamos el flujo energético, la acústica, la ergonomía y el potencial de cada zona. Identificamos los puntos exactos que están frenando tus ingresos.',
  },
  {
    num: '02',
    title: 'Estrategia con ROI proyectado',
    description:
      'Presentamos una propuesta de diseño con proyección de retorno de inversión, selección de materiales y cronograma realista para tu tipo de negocio.',
  },
  {
    num: '03',
    title: 'Ejecución supervisada',
    description:
      'La Arq. Viviana Pacheco supervisa cada detalle de la obra: materiales, proveedores, tiempos. Sin sorpresas. Sin costos ocultos.',
  },
  {
    num: '04',
    title: 'Resultados medibles',
    description:
      'A 90 días del proyecto, medimos contigo: ocupación, ticket promedio, reseñas online y engagement en redes. El diseño se justifica con números.',
  },
];

// ─── Case Studies ─────────────────────────────────────────────────────────────
export const CASE_STUDIES: CaseStudy[] = [
  {
    id: 'baccarat',
    title: 'Baccarat Hotel & Residences',
    location: 'Nueva York, EE.UU.',
    sector: 'Hotelería Ultra-Lujo',
    badge: 'EXITOSO',
    metric: '#1',
    metricLabel: 'tarifas récord al inaugurar',
    insight:
      'El agua en la entrada atrapa el Chi de la Quinta Avenida y lo dirige hacia las boutiques y restaurantes.',
    description:
      'La firma contrató maestros de Feng Shui para equilibrar la energía del cristal con elementos de Tierra y Fuego. Fuentes estratégicas en la entrada capturaron el Chi de Manhattan y lo dirigieron al comercio interior.',
    image: '/images/case_baccarat_2.png',
  },
  {
    id: 'hakkasan',
    title: 'Hakkasan Restaurant',
    location: 'Londres · Miami · Las Vegas',
    sector: 'Restauración Estrella Michelin',
    badge: 'EXITOSO',
    metric: 'Global',
    metricLabel: 'de local a imperio multimillonario',
    insight:
      'Las pantallas de madera tallada rompen las "flechas venenosas" y crean flujo sinuoso que invita a un consumo prolongado.',
    description:
      'El diseñador Christian Liaigre trabajó con maestros de Feng Shui para crear el icónico restaurante cantonés. Iluminación Yin calibrada genera relajación y misterio, incitando a los clientes a permanecer más y gastar más.',
    image: '/images/case_hakkasan_2.png',
  },
  {
    id: 'tao',
    title: 'TAO Nightclub & Restaurant',
    location: 'The Venetian, Las Vegas',
    sector: 'Entretenimiento Nocturno',
    badge: 'EXITOSO',
    metric: '$60M',
    metricLabel: 'USD anuales — récord histórico EE.UU.',
    insight:
      'El agua en movimiento actúa como imán de dinero; los koi disipan la energía negativa de las multitudes Yang.',
    description:
      'Un Buda de 5 metros flota sobre un estanque de koi real. El agua activa el elemento riqueza del Feng Shui, mientras el Buda equilibra la energía Yang agresiva de la noche. Por casi una década fue el bar-restaurante con mayores ingresos de EE.UU.',
    image: '/images/case_tao_2.png',
  },
  {
    id: 'hsbc',
    title: 'HSBC Hong Kong HQ',
    location: 'Hong Kong',
    sector: 'Banca y Finanzas',
    badge: 'EXITOSO',
    metric: '+ Chi',
    metricLabel: 'atrio que absorbe la energía del puerto',
    insight:
      'El atrio hueco y las escaleras en ángulos específicos no son capricho estético: absorben y retienen el Chi del puerto.',
    description:
      'El icónico edificio de Norman Foster fue construido consultando a maestros de Feng Shui. Su atrio abierto capta la energía del puerto de Hong Kong y la canaliza al interior.',
    image: '/images/case_hsbc_2.png',
  },
  {
    id: 'mgm',
    title: 'MGM Grand Casino',
    location: 'Las Vegas Strip',
    sector: 'Entretenimiento y Juego',
    badge: 'MEJORAS',
    metric: 'Millones',
    metricLabel: 'invertidos en rediseñar la entrada',
    insight:
      'Caminar por las fauces de un león simboliza "ser devorado" — espantó al mercado asiático de alto nivel.',
    description:
      'El casino tuvo que gastar millones rediseñando su entrada: los clientes debían caminar por las fauces abiertas de un león. En el diseño energético, simboliza pérdida económica. Una vez reubicada, el flujo de clientes VIP asiáticos se recuperó.',
    image: '/images/case_mgm_2.png',
  },
  {
    id: 'bodyshop',
    title: 'The Body Shop',
    location: 'Flagships Globales',
    sector: 'Retail de Bienestar',
    badge: 'EXITOSO',
    metric: 'Miles',
    metricLabel: 'de tiendas con alta retención por m²',
    insight:
      'Entradas despejadas, colores del elemento Madera en zonas de salud y atmósfera de calma — el Chi entra libremente.',
    description:
      'Anita Roddick aplicó principios de Feng Shui en sus flagships: entradas completamente despejadas, colores verdes en las zonas Bagua de salud. El resultado: altísima retención de clientes y expansión global.',
    image: '/images/case_bodyshop_2.png',
  },
];

// ─── Gallery ──────────────────────────────────────────────────────────────────
export const GALLERY_IMAGES: GalleryImage[] = [
  { url: '/images/cdmx_restaurante_2.png', alt: 'Restaurante sensorial Polanco CDMX' },
  { url: '/images/cdmx_room_airbnb_2.png', alt: 'Cuarto Airbnb lujoso Roma Norte' },
  { url: '/images/cdmx_hotel_2.png',       alt: 'Lobby hotel boutique CDMX' },
  { url: '/images/cdmx_terraza_2.png',     alt: 'Terraza rooftop con vista CDMX' },
  { url: '/images/hero_biofil_2.png',      alt: 'Diseño biofílico muro verde' },
  { url: '/images/cdmx_airbnb_2.png',      alt: 'Airbnb editorial Roma Norte' },
];
